import type {
  ActivityEntry,
  CreateEventPayload,
  MemberEntry,
} from "../repositories/eventsRepository";
import { formatLocalDateToISODate } from "./dateTransformers";
import { getSelectedFromArrays } from "./getSelectedFromArrays";

export interface BuildCreateEventPayloadInput {
  name: string;
  type: string;
  description?: string;
  terreiroId: string;
  date?: string | Date;
  isAllDay?: boolean;
  startTime?: string;
  endTime?: string;
  isTemplate?: boolean;
  qrCheckinEnabled?: boolean;
  checkinStartWindowMinutes?: number;
  checkinEndWindowMinutes?: number;
  location?: CreateEventPayload["location"];
  members?: Array<string | { id: string; fromActivity?: boolean }>;
  insumos?: Array<{ id: string | number; quantity: number; fromActivity?: boolean }>;
  activities?: Array<{
    id: string;
    startTime?: string;
    endTime?: string;
    checkinStartWindowMinutes?: number;
    checkinEndWindowMinutes?: number;
  }>;
  requiredActivityIds?: string[];
  minConfirmedMembers?: number;
}

const resolveAllDayTimes = (isAllDay: boolean, startTime?: string, endTime?: string) => {
  if (!isAllDay) {
    return {
      startTime: startTime ?? "",
      endTime: endTime ?? "",
    };
  }
  return {
    startTime: startTime || "00:00:00",
    endTime: endTime || "23:59:59",
  };
};

export const buildCreateEventPayload = (
  input: BuildCreateEventPayloadInput,
): CreateEventPayload => {
  const isAllDay = Boolean(input.isAllDay);
  const { startTime, endTime } = resolveAllDayTimes(
    isAllDay,
    input.startTime,
    input.endTime,
  );

  const activityEntries: ActivityEntry[] = (input.activities ?? []).map((a) => ({
    id: a.id,
    startTime: a.startTime ?? "",
    endTime: a.endTime ?? "",
    qrReleaseOffsetMinutes:
      a.checkinStartWindowMinutes ?? input.checkinStartWindowMinutes ?? 10,
    qrCheckinGraceMinutes:
      a.checkinEndWindowMinutes ?? input.checkinEndWindowMinutes ?? 15,
  }));

  const members: MemberEntry[] = (input.members ?? []).map((m) => {
    if (typeof m === "string") return m;
    return m.fromActivity ? { id: m.id, fromActivity: true } : m.id;
  });

  const insumos = (input.insumos ?? []).map(({ id, quantity, fromActivity }) => ({
    id: String(id),
    quantity,
    ...(fromActivity ? { fromActivity: true } : {}),
  }));

  const location = input.location ?? {
    isInTerreiro: true,
    street: "",
    number: "",
    neighborhood: "",
    city: "",
    state: "",
  };

  const dateValue =
    input.date instanceof Date
      ? formatLocalDateToISODate(input.date)
      : input.date ?? formatLocalDateToISODate(new Date());

  return {
    name: input.name,
    type: input.type,
    description: input.description ?? "",
    terreiroId: input.terreiroId,
    date: dateValue,
    isAllDay,
    startTime,
    endTime,
    isTemplate: Boolean(input.isTemplate),
    location,
    members,
    insumos,
    activities: activityEntries,
    requiredActivityIds: input.requiredActivityIds ?? [],
    minConfirmedMembers: input.minConfirmedMembers,
    qrCheckinEnabled: input.qrCheckinEnabled !== false,
    checkinStartWindowMinutes: input.checkinStartWindowMinutes ?? 10,
    checkinEndWindowMinutes: input.checkinEndWindowMinutes ?? 15,
    qrReleaseOffsetMinutes: input.checkinStartWindowMinutes ?? 10,
    qrCheckinGraceMinutes: input.checkinEndWindowMinutes ?? 15,
  };
};

export const buildCreateEventPayloadFromOnboarding = (
  data: Record<string, unknown>,
  terreiroId: string,
): CreateEventPayload => {
  const payload = data as CreateEventPayload & {
    date: string | Date;
    members?: Array<{ id: string; fromActivity?: boolean; isSelected?: boolean }>;
    insumos?: Array<{
      id: string | number;
      quantity: number;
      fromActivity?: boolean;
      isSelected?: boolean;
    }>;
    activities?: Array<{
      id: string;
      startTime?: string;
      endTime?: string;
      checkinStartWindowMinutes?: number;
      checkinEndWindowMinutes?: number;
      isSelected?: boolean;
    }>;
  };

  const selectedActivities = getSelectedFromArrays(payload.activities ?? []);
  const selectedMembers = getSelectedFromArrays(payload.members ?? []);
  const selectedInsumos = getSelectedFromArrays(payload.insumos ?? []);

  return buildCreateEventPayload({
    name: payload.name,
    type: payload.type,
    description: payload.description,
    terreiroId,
    date: payload.date,
    isAllDay: payload.isAllDay,
    startTime: payload.startTime,
    endTime: payload.endTime,
    isTemplate: payload.isTemplate,
    qrCheckinEnabled: (payload as { qrCheckinEnabled?: boolean }).qrCheckinEnabled,
    checkinStartWindowMinutes: payload.checkinStartWindowMinutes,
    checkinEndWindowMinutes: payload.checkinEndWindowMinutes,
    location: payload.location,
    members: selectedMembers.map((member): string | { id: string; fromActivity?: boolean } => {
      if (typeof member === "string") return member;
      return member.fromActivity
        ? { id: member.id, fromActivity: true }
        : member.id;
    }),
    insumos: selectedInsumos.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      fromActivity: item.fromActivity,
    })),
    activities: selectedActivities.map((activity) => {
      if (typeof activity === "string") {
        return { id: activity };
      }
      return {
        id: activity.id,
        startTime: activity.startTime,
        endTime: activity.endTime,
        checkinStartWindowMinutes:
          activity.checkinStartWindowMinutes ?? payload.checkinStartWindowMinutes,
        checkinEndWindowMinutes:
          activity.checkinEndWindowMinutes ?? payload.checkinEndWindowMinutes,
      };
    }),
    requiredActivityIds: payload.requiredActivityIds,
    minConfirmedMembers: payload.minConfirmedMembers,
  });
};
